import { get } from 'lodash'

const HEUREKA_DIRECT_SCRIPT_ID = 'HeurekaDirectConversionsSDK'
const HEUREKA_SENT_ORDERS_STORAGE_KEY = 'CVB_HEUREKA_DIRECT_SENT_ORDERS'
const HEUREKA_LAST_PRODUCT_DETAIL_PATH_KEY = '__CVB_HEUREKA_LAST_PRODUCT_DETAIL_PATH__'

function getDirectConversionsConfig(vm) {
  return get(vm, '$contentSettings.events.heureka.directConversions', {})
}

function getPortalHost(portal) {
  if (portal === 'sk') {
    return 'www.heureka.sk'
  }

  return 'www.heureka.cz'
}

function getPortalCode(portal) {
  return portal === 'sk' ? 'sk' : 'cz'
}

function isClient() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function isAllowedSite(vm, config) {
  const allowedSites = Array.isArray(config.allowedSites) ? config.allowedSites : []
  const site = get(vm, '$env.SITE', '')

  return allowedSites.includes(site)
}

function getSdkUrl(portal, pageType) {
  return `https://${getPortalHost(portal)}/ocm/sdk.js?version=2&page=${pageType}`
}

function resetHeurekaQueue(portal) {
  const portalCode = getPortalCode(portal)

  window.ROIDataObject = 'heureka'

  const queue = function() {
    ;(queue.q = queue.q || []).push(arguments)
  }

  queue.q = []
  queue.c = portalCode
  window.heureka = queue
}

function removeExistingSdk() {
  const existingScript = document.getElementById(HEUREKA_DIRECT_SCRIPT_ID)

  if (existingScript) {
    existingScript.remove()
  }
}

function injectSdk(portal, pageType) {
  const script = document.createElement('script')
  script.id = HEUREKA_DIRECT_SCRIPT_ID
  script.async = true
  script.src = getSdkUrl(portal, pageType)
  document.head.appendChild(script)
}

function initializeSdk(vm, pageType) {
  if (!isHeurekaDirectConversionsEnabled(vm)) {
    return false
  }

  const config = getDirectConversionsConfig(vm)
  const portal = config.portal || 'cz'

  removeExistingSdk()
  resetHeurekaQueue(portal)
  injectSdk(portal, pageType)

  return true
}

function getStoredOrderIds() {
  if (!isClient()) {
    return []
  }

  const sentOrders = window.localStorage.getItem(HEUREKA_SENT_ORDERS_STORAGE_KEY) || ''

  return sentOrders
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

function logSentOrder(orderIdentifier) {
  const sentOrders = getStoredOrderIds()
  window.localStorage.setItem(HEUREKA_SENT_ORDERS_STORAGE_KEY, [...sentOrders, orderIdentifier].join(','))
}

function hasOrderBeenSent(orderIdentifier) {
  return getStoredOrderIds().includes(orderIdentifier)
}

function formatValue(value) {
  if (value === null || value === undefined) {
    return ''
  }

  return String(value)
}

function hasPrice(value) {
  const numericValue = Number(value)

  return Number.isFinite(numericValue) && numericValue !== 0
}

function getItemId(item, source) {
  if (source === 'product.marketingCode') {
    return get(item, 'product.marketingCode', '')
  }

  if (source === 'catalogCode') {
    return get(item, 'shoppable.catalogCode') || get(item, 'product.catalogCode', '')
  }

  return get(item, 'shoppable.marketingCode', '')
}

function getTrackableItems(order, itemIdSource) {
  return (get(order, 'items', []) || []).filter(item => {
    const hasDiscount = Boolean(get(item, 'attributes.discount'))

    return !hasDiscount && get(item, 'shoppable') && get(item, 'product') && getItemId(item, itemIdSource)
  })
}

function getDiscountItems(order) {
  return (get(order, 'items', []) || [])
    .filter(item => Boolean(get(item, 'attributes.discount')) && hasPrice(get(item, 'prices.unitWithTax')))
    .map(item => ({
      name: get(item, 'name') || get(item, 'shoppable.name') || get(item, 'product.name') || 'Sleva',
      priceVat: get(item, 'prices.unitWithTax'),
      quantity: get(item, 'amount') || 1,
    }))
}

function getAdditionalItems(order, config) {
  if (!config.includeAdditionalItems) {
    return []
  }

  return [
    {
      name: get(order, 'shipper.name') || 'Doprava',
      priceVat: get(order, 'prices.shipperPriceWithTax'),
      quantity: 1,
    },
    {
      name: get(order, 'payment.name') || 'Platba',
      priceVat: get(order, 'prices.paymentPriceWithTax'),
      quantity: 1,
    },
    ...getDiscountItems(order),
  ].filter(item => hasPrice(item.priceVat))
}

function getOrderIdentifier(order) {
  return formatValue(get(order, 'code') || get(order, 'id'))
}

function getOrderCurrency(vm, order) {
  return (
    get(order, 'currencyCode') ||
    get(order, 'currency.code') ||
    get(vm, '$store.state.currency.activeCurrency.code') ||
    'CZK'
  )
}

function getCurrentPath(vm) {
  const routePath = get(vm, '$route.fullPath') || get(vm, '$route.path')

  if (routePath) {
    return String(routePath)
  }

  if (isClient() && window.location) {
    return `${window.location.pathname}${window.location.search}`
  }

  return ''
}

function hasTrackedCurrentProductDetail(vm) {
  return isClient() && window[HEUREKA_LAST_PRODUCT_DETAIL_PATH_KEY] === getCurrentPath(vm)
}

function markCurrentProductDetailTracked(vm) {
  if (!isClient()) {
    return
  }

  window[HEUREKA_LAST_PRODUCT_DETAIL_PATH_KEY] = getCurrentPath(vm)
}

function getHeurekaKey(vm) {
  return (
    get(vm, '$contentSettings.events.heureka.directConversions.apiKey') ||
    get(vm, '$store.state.globals.settings.integrations.heureka_public_key', '')
  )
}

export function isHeurekaDirectConversionsEnabled(vm) {
  const config = getDirectConversionsConfig(vm)

  return Boolean(isClient() && config.enable && isAllowedSite(vm, config))
}

export function trackHeurekaProductDetail(vm) {
  if (!initializeSdk(vm, 'product_detail') || hasTrackedCurrentProductDetail(vm)) {
    return false
  }

  markCurrentProductDetailTracked(vm)

  return true
}

export function trackHeurekaThankYou(vm, order) {
  if (!isHeurekaDirectConversionsEnabled(vm) || !order) {
    return false
  }

  const config = getDirectConversionsConfig(vm)
  const orderIdentifier = getOrderIdentifier(order)
  const heurekaKey = getHeurekaKey(vm)
  const trackableItems = getTrackableItems(order, config.itemIdSource)
  const additionalItems = getAdditionalItems(order, config)

  if (!heurekaKey || !orderIdentifier || !trackableItems.length) {
    return false
  }

  if (hasOrderBeenSent(orderIdentifier)) {
    return false
  }

  initializeSdk(vm, 'thank_you')

  window.heureka('authenticate', heurekaKey)
  window.heureka('set_order_id', orderIdentifier)

  trackableItems.forEach(item => {
    window.heureka(
      'add_product',
      formatValue(getItemId(item, config.itemIdSource)),
      formatValue(get(item, 'shoppable.name') || get(item, 'product.name')),
      formatValue(get(item, 'prices.unitWithTax')),
      formatValue(get(item, 'amount')),
    )
  })

  additionalItems.forEach(item => {
    window.heureka(
      'add_additional_item',
      formatValue(item.name),
      formatValue(item.priceVat),
      formatValue(item.quantity),
    )
  })

  window.heureka('set_total_vat', formatValue(get(order, 'prices.totalWithTax')))
  window.heureka('set_currency', formatValue(getOrderCurrency(vm, order)))
  window.heureka('send', 'Order')

  logSentOrder(orderIdentifier)

  return true
}
